import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QBox, QButton, QLoader, QStack, QText, QTextField } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { ConversationListItem } from '@captivateiq/assistant/types';
import { GroupedConversationList } from '../../ConversationHistory/GroupedConversationList';
import { CONVERSATIONS_PAGE_SIZE } from '../../ConversationHistory/constants';
import { filterConversationsBySearch } from '../../ConversationHistory/filterConversationsBySearch';
import { TabLayout } from '../TabLayout';
import { expandedTextColumnSx } from '../shared/expandedTextColumnSx';

export const HistoryTab = () => {
  const { t } = useTranslation('assistant');
  const { getConversations, backToChat, isExpanded } = useAIShellContext();

  const [conversations, setConversations] = useState<Array<ConversationListItem>>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const result = await getConversations(CONVERSATIONS_PAGE_SIZE, 0);
        setConversations(result?.data || []);
        setTotalCount(result?.total_count ?? 0);
      } catch (error) {
        console.error('HistoryTab: Error fetching conversations:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [getConversations]);

  const hasMore = conversations.length < totalCount;

  const handleLoadMore = useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const result = await getConversations(CONVERSATIONS_PAGE_SIZE, conversations.length);
      setConversations((prev) => [...prev, ...(result?.data ?? [])]);
      setTotalCount(result?.total_count ?? totalCount);
    } catch (error) {
      console.error('HistoryTab: Error loading more conversations:', error);
      setHasError(true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [getConversations, conversations.length, totalCount]);

  const filtered = useMemo(() => filterConversationsBySearch(conversations, query), [conversations, query]);

  if (isLoading) {
    return (
      <QBox
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <QLoader />
      </QBox>
    );
  }

  if (hasError) {
    return (
      <QStack
        direction="column"
        sx={{
          alignItems: 'stretch',
          height: '100%',
        }}
      >
        <QText sx={{ textAlign: 'center', mt: 4 }}>{t('messages.error_loading_chats')}</QText>
      </QStack>
    );
  }

  return totalCount ? (
    <TabLayout
      bottomContent={
        hasMore && (
          <QBox sx={expandedTextColumnSx(isExpanded)}>
            <QButton
              disabled={isLoadingMore}
              sx={{
                borderRadius: '4px',
                margin: 0,
                width: '100%',
              }}
              variant="outlined"
              onClick={handleLoadMore}
            >
              {isLoadingMore ? <QLoader /> : t('button_labels.load_more')}
            </QButton>
          </QBox>
        )
      }
      contentContainerProps={{
        sx: {
          padding: '4px',
          gap: '4px',
          backgroundColor: 'neutral.primary.white',
          ...expandedTextColumnSx(isExpanded),
        },
      }}
      topContent={
        <QStack spacing="12px" sx={expandedTextColumnSx(isExpanded)}>
          <QStack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <QText sx={{ fontWeight: 600 }}>{t('headings.all_chats_count', { count: totalCount })}</QText>
            <QButton color="primary" size="small" startIcon={faChevronLeft} variant="text" onClick={backToChat}>
              {t('button_labels.back_to_chat')}
            </QButton>
          </QStack>
          <QTextField
            fullWidth
            placeholder={t('input_placeholders.search') as string}
            startIcon={faMagnifyingGlass}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </QStack>
      }
    >
      <GroupedConversationList
        conversations={filtered}
        emptyState={
          <QText sx={{ textAlign: 'center', mt: 4 }}>
            {t(query ? 'messages.no_chats_match_search' : 'messages.no_chats')}
          </QText>
        }
      />
    </TabLayout>
  ) : (
    <QStack
      direction="column"
      sx={{
        alignItems: 'stretch',
        height: '100%',
      }}
    >
      <QText sx={{ textAlign: 'center', mt: 4 }}>{t('messages.no_chats')}</QText>
    </QStack>
  );
};
